// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Bill create / edit / view page (Stage 6P.4).
//
// Routes:
//   /erp/purchase/bills/new                 → blank create
//   /erp/purchase/bills/:id                 → edit / view (status-gated)
//   /erp/purchase/bills/from-po/:poId       → server-prefilled from PO
//   /erp/purchase/bills/from-grn/:grnId     → server-prefilled from GRN
//
// Hosts the header form (PB meta + supplier + state + matching mode),
// an item-type-aware inline line grid (GLASS area math, PROFILE /
// UPVC / ALUMINIUM length math, SIMPLE qty for everything else), a
// live summary panel, a match panel showing blockers / warnings /
// info and a workflow toolbar (Save Draft → Match → Approve / Cancel).
//
// Backend (`purchaseBill.service`, `purchaseBillCalculation.service`,
// `purchaseBillMatching.service`) remains the source of truth for ALL
// math (bill_sqmt / bill_sqft / bill_total_length, gross / discount /
// taxable / CGST / SGST / IGST), all match deltas (qty / rate / tax /
// amount / duplicate-invoice / over-billing / missing-GRN) and every
// state transition. The frontend mirrors via purchaseBillHelpers so
// the operator gets instant feedback, but never wins.
//
// Three (really four) load paths:
//   • edit (id)         → purchaseBillApi.getById(id)
//   • from-po (poId)    → purchaseBillApi.billableBalanceForPo(poId)
//                         + purchaseOrdersApi.get(poId) → in-memory draft
//                         via buildBillFromPoLines (NOT auto-persisted;
//                         first Save Draft calls
//                         purchaseBillApi.createFromPo).
//   • from-grn (grnId)  → purchaseBillApi.billableBalanceForGrn(grnId)
//                         + goodsReceiptsApi.get(grnId) → in-memory draft
//                         via buildBillFromGrnLines (first save uses
//                         purchaseBillApi.createFromGrn).
//   • new               → empty draft.
//
// Style: mirrors GoodsReceiptFormPage / PurchaseOrderFormPage density
// (slate-50 uppercase header, 22 px rows, sticky toolbar, borderless
// inline cell editors, ⋯ row actions). Reuses the .grn-* class names
// since the layout is structurally identical and the GRN CSS is
// already loaded by the bundle.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileDoneOutlined,
  LinkOutlined,
  LockOutlined,
  MoreOutlined,
  SaveOutlined,
  SearchOutlined,
  StopOutlined,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { purchaseBillApi } from './purchaseBillApi';
import ItemMasterSelect from './ItemMasterSelect';
import { goodsReceiptsApi } from './grnApi';
import { purchaseOrdersApi } from './purchaseApi';
import {
  GLASS_FAMILY_TYPES,
  GST_RATE_MAX,
  GST_RATE_MIN,
  ITEM_TYPES,
  LENGTH_BASED_UOMS,
  PB_MATCHING_TYPES,
  PROFILE_FAMILY_TYPES,
  buildBillFromGrnLines,
  buildBillFromPoLines,
  calculatePurchaseBillLine,
  calculatePurchaseBillTotals,
  canApprovePurchaseBill,
  canCancelPurchaseBill,
  canEditPurchaseBill,
  canMatchPurchaseBill,
  matchStatusColor,
  matchStatusLabel,
  statusColor,
  statusLabel,
  summarizeMatchResults,
  validatePurchaseBill
} from './purchaseBillHelpers';
import SupplierMasterSelect from './SupplierMasterSelect';
// PB layout mirrors the GRN form 1:1 — reuse the existing CSS so the
// `grn-*` class names render identically here (no new CSS file).
import './GoodsReceiptFormPage.css';

// ─────────────────────────────────────────────────────────────────
// Static option lists. UOMs mirror the GRN editor so operators see
// the same vocabulary up and down the chain.
// ─────────────────────────────────────────────────────────────────
const UOM_OPTIONS = Object.freeze([
  'PCS', 'NOS', 'KG', 'GRM', 'LTR', 'MTR', 'RFT', 'RMT',
  'SQMT', 'SQFT', 'BOX', 'SET', 'PAIR', 'BUNDLE', 'DOZ', 'ROLL', 'SHEET'
].map((v) => ({ value: v, label: v })));

const ITEM_TYPE_OPTIONS = Object.freeze(
  ITEM_TYPES.map((v) => ({ value: v, label: v }))
);

const MATCHING_TYPE_OPTIONS = Object.freeze(
  PB_MATCHING_TYPES.map((v) => ({
    value: v,
    label: v === 'TWO_WAY' ? '2-Way (PO ↔ Bill)' : '3-Way (PO ↔ GRN ↔ Bill)'
  }))
);

const GLASS_TYPES   = new Set(GLASS_FAMILY_TYPES);

// Item-type families exempt from the Item Master lock (free-text allowed).
// Everything else must be picked from the master. Mirrors the backend
// MASTER_OPTIONAL_ITEM_TYPES in services/erp/purchase/itemMasterGuard.js.
const MASTER_OPTIONAL_TYPES = new Set(['SERVICE', 'OTHER']);
const PROFILE_TYPES = new Set(PROFILE_FAMILY_TYPES);
const LENGTH_UOMS   = new Set(LENGTH_BASED_UOMS);

// ─────────────────────────────────────────────────────────────────
// Tiny date / number helpers — kept out of the pure ESM helpers
// module so node:test can exercise the math without a dayjs install.
// ─────────────────────────────────────────────────────────────────
function todayDayjs() {
  return dayjs().startOf('day');
}

function isoToDayjs(v) {
  if (v === null || v === undefined || v === '') return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
}

function dayjsToIso(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

const _fmtDim = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4
});
function dim(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '';
  return _fmtDim.format(v);
}

const _fmtQty = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4
});
function qty(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return _fmtQty.format(v);
}

const _fmtMoney = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0.00';
  return _fmtMoney.format(v);
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Stable client-row id minter for freshly-added blank lines.
let _localRowSeq = 0;
function nextLocalRowId() {
  _localRowSeq += 1;
  return `pb-local-${Date.now()}-${_localRowSeq}`;
}

function makeEmptyLine() {
  return calculatePurchaseBillLine(
    {
      _clientRowId:     nextLocalRowId(),
      _dirty:           true,
      item_type:        'OTHER',
      item_name:        '',
      uom:              'PCS',
      bill_qty:         0,
      bill_rate:        0,
      discount_percent: 0,
      gst_rate:         0,
      remarks:          ''
    },
    {}
  );
}

// Header defaults for create mode.
function makeEmptyHeader() {
  return {
    id:                  null,
    billNo:              null,
    billDate:            todayDayjs(),
    supplierInvoiceNo:   '',
    supplierInvoiceDate: todayDayjs(),
    status:              'DRAFT',
    matchStatus:         'PENDING',
    matchingType:        'TWO_WAY',

    purchaseOrderId:     null,
    poNumberSnapshot:    null,
    poDateSnapshot:      null,

    goodsReceiptId:      null,
    grnNumberSnapshot:   null,
    grnDateSnapshot:     null,

    supplierId:          null,
    supplierName:        '',
    supplierGstin:       '',
    supplierStateCode:   '',
    companyStateCode:    '',
    supplierContact:     '',
    supplierAddress:     '',

    roundOff:            0,
    remarks:             '',

    approvedBy:          null,
    approvedAt:          null,
    cancelledBy:         null,
    cancelledAt:         null,
    cancellationReason:  null,

    matchResults:        [],

    lines: []
  };
}

// Map editor header state → AntD Form values. Date fields stay as
// dayjs so the pickers work; everything else is plain.
function headerToFormValues(h) {
  return {
    billDate:            h.billDate || null,
    supplierInvoiceNo:   h.supplierInvoiceNo || '',
    supplierInvoiceDate: h.supplierInvoiceDate || null,
    matchingType:        h.matchingType || 'TWO_WAY',
    supplierName:        h.supplierName || '',
    supplierGstin:       h.supplierGstin || '',
    supplierStateCode:   h.supplierStateCode || '',
    companyStateCode:    h.companyStateCode || '',
    poNumberSnapshot:    h.poNumberSnapshot || '',
    grnNumberSnapshot:   h.grnNumberSnapshot || '',
    roundOff:            Number(h.roundOff) || 0,
    remarks:             h.remarks || ''
  };
}

// ─────────────────────────────────────────────────────────────────
// API → editor shape — Purchase Bill (header + lines)
// ─────────────────────────────────────────────────────────────────
function normalizePurchaseBill(api) {
  const safe  = api && typeof api === 'object' ? api : {};
  const lines = Array.isArray(safe.lines) ? safe.lines : [];
  return {
    id:                  safe.id                  || null,
    billNo:              safe.billNo              || safe.bill_no              || null,
    billDate:            safe.billDate            || safe.bill_date            || null,
    supplierInvoiceNo:   safe.supplierInvoiceNo   || safe.supplier_invoice_no   || '',
    supplierInvoiceDate: safe.supplierInvoiceDate || safe.supplier_invoice_date || null,
    status:              safe.status              || 'DRAFT',
    matchStatus:         safe.matchStatus         || safe.match_status         || 'PENDING',
    matchingType:        safe.matchingType        || safe.matching_type        || 'TWO_WAY',

    purchaseOrderId:     safe.purchaseOrderId     || safe.purchase_order_id    || safe.poId || safe.po_id || null,
    poNumberSnapshot:    safe.poNumberSnapshot    || safe.po_number_snapshot   || null,
    poDateSnapshot:      safe.poDateSnapshot      || safe.po_date_snapshot     || null,

    goodsReceiptId:      safe.goodsReceiptId      || safe.goods_receipt_id     || safe.grnId || safe.grn_id || null,
    grnNumberSnapshot:   safe.grnNumberSnapshot   || safe.grn_number_snapshot  || null,
    grnDateSnapshot:     safe.grnDateSnapshot     || safe.grn_date_snapshot    || null,

    supplierId:          safe.supplierId          || safe.supplier_id          || null,
    supplierName:        safe.supplierName        || safe.supplier_name        || '',
    supplierGstin:       safe.supplierGstin       || safe.supplier_gstin       || '',
    supplierStateCode:   safe.supplierStateCode   || safe.supplier_state_code  || '',
    companyStateCode:    safe.companyStateCode    || safe.company_state_code   || '',
    supplierContact:     safe.supplierContact     || safe.supplier_contact     || '',
    supplierAddress:     safe.supplierAddress     || safe.supplier_address     || '',

    roundOff:            Number(safe.roundOff || safe.round_off || 0),
    remarks:             safe.remarks || '',

    approvedBy:          safe.approvedBy          || safe.approved_by          || null,
    approvedAt:          safe.approvedAt          || safe.approved_at          || null,
    cancelledBy:         safe.cancelledBy         || safe.cancelled_by         || null,
    cancelledAt:         safe.cancelledAt         || safe.cancelled_at         || null,
    cancellationReason:  safe.cancellationReason  || safe.cancellation_reason  || null,

    matchResults:        Array.isArray(safe.matchResults) ? safe.matchResults
                          : (Array.isArray(safe.match_results) ? safe.match_results : []),

    lines: lines.map((ln) => calculatePurchaseBillLine(ln, {
      supplier_state_code: safe.supplierStateCode || safe.supplier_state_code,
      company_state_code:  safe.companyStateCode  || safe.company_state_code
    }))
  };
}

// Build the JSON payload the API expects. Backend recomputes all
// derived totals — we send raw inputs + line ids + status/matching
// hints.
function buildPurchaseBillPayload(state) {
  const lines = (Array.isArray(state.lines) ? state.lines : []).map((ln) => {
    const itemTypeUp = (ln.item_type || ln.itemType || '').toUpperCase();
    const isGlass    = GLASS_TYPES.has(itemTypeUp);
    const isProfile  = PROFILE_TYPES.has(itemTypeUp);
    const rate       = Number(ln.bill_rate ?? ln.billRate ?? ln.rate ?? 0);
    const widthMm    = isGlass   ? Number(ln.bill_width_mm    ?? ln.billWidthMm    ?? ln.width_mm     ?? 0) : null;
    const heightMm   = isGlass   ? Number(ln.bill_height_mm   ?? ln.billHeightMm   ?? ln.height_mm    ?? 0) : null;
    const lengthVal  = isProfile ? Number(ln.bill_length_value ?? ln.billLengthValue ?? ln.length_value ?? 0) : null;
    return {
      line_no:                Number(ln.line_no || ln.lineNo) || undefined,
      purchase_order_line_id: ln.purchase_order_line_id || ln.purchaseOrderLineId || null,
      goods_receipt_line_id:  ln.goods_receipt_line_id  || ln.goodsReceiptLineId  || null,
      item_id:                ln.item_id    || ln.itemId    || null,
      item_code:              ln.item_code  || ln.itemCode  || null,
      item_name:              ln.item_name  || ln.itemName  || null,
      item_type:              ln.item_type  || ln.itemType  || null,
      uom:                    ln.uom || null,
      hsn_code:               ln.hsn_code || ln.hsnCode || null,
      sac_code:               ln.sac_code || ln.sacCode || null,

      bill_qty:               Number(ln.bill_qty ?? ln.billQty ?? 0),
      // Canonical keys the backend validators require (rate / width_mm /
      // height_mm / length_value — backend stripUnknown drops the bill_*
      // mirrors). The bill_* keys are kept for in-app readback.
      rate:                   rate,
      bill_rate:              rate,
      discount_percent:       Number(ln.discount_percent ?? ln.discountPercent ?? 0),
      gst_rate:               Number(ln.gst_rate ?? ln.gstRate ?? 0),

      width_mm:               widthMm,
      bill_width_mm:          widthMm,
      height_mm:              heightMm,
      bill_height_mm:         heightMm,
      length_value:           lengthVal,
      bill_length_value:      lengthVal,

      remarks:                ln.remarks || null
    };
  });

  return {
    bill_date:             dayjsToIso(state.billDate),
    supplier_invoice_no:   state.supplierInvoiceNo || null,
    supplier_invoice_date: dayjsToIso(state.supplierInvoiceDate),
    status:                state.status || 'DRAFT',
    matching_type:         state.matchingType || 'TWO_WAY',

    purchase_order_id:     state.purchaseOrderId || null,
    goods_receipt_id:      state.goodsReceiptId  || null,

    supplier_id:           state.supplierId   || null,
    supplier_name:         state.supplierName || null,
    supplier_gstin:        state.supplierGstin || null,
    supplier_state_code:   state.supplierStateCode || null,
    company_state_code:    state.companyStateCode  || null,

    round_off:             Number(state.roundOff) || 0,
    remarks:               state.remarks || null,

    lines
  };
}

// ─────────────────────────────────────────────────────────────────
// PO picker — used in /new mode to attach a source PO.
// Filters to POs eligible for billing.
// ─────────────────────────────────────────────────────────────────
function PurchaseOrderPicker({ value, onPick, disabled }) {
  const [open, setOpen]       = useState(false);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows]       = useState([]);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = {
        page:     1,
        pageSize: 25,
        status:   'PLACED,PARTIALLY_RECEIVED,RECEIVED'
      };
      if (q) params.search = q;
      const out = await purchaseOrdersApi.list(params);
      const list = Array.isArray(out)
        ? out
        : (out && Array.isArray(out.rows) ? out.rows
          : (out && Array.isArray(out.data) ? out.data : []));
      setRows(list);
    } catch (e) {
      message.error(errMessage(e, 'Failed to load purchase orders.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(keyword.trim()), 220);
    return () => clearTimeout(t);
  }, [open, keyword, search]);

  return (
    <>
      <Input
        readOnly
        value={value || ''}
        disabled={disabled}
        placeholder="Click to pick a PO…"
        suffix={
          <Button
            type="text"
            size="small"
            icon={<SearchOutlined />}
            disabled={disabled}
            onClick={() => setOpen(true)}
          />
        }
        onClick={() => { if (!disabled) setOpen(true); }}
      />
      <Modal
        open={open}
        title="Select Purchase Order"
        onCancel={() => setOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder="Search by PO number, supplier…"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={(v) => search(v.trim())}
          className="grn-po-picker__search"
        />
        <Table
          className="grn-po-picker__grid"
          size="small"
          loading={loading}
          rowKey={(r) => r.id || r.po_id}
          dataSource={rows}
          pagination={false}
          scroll={{ y: 360 }}
          columns={[
            {
              title: 'PO No', dataIndex: 'poNo', width: 140,
              render: (v, r) => v || r.po_no || '—'
            },
            {
              title: 'PO Date', dataIndex: 'poDate', width: 110,
              render: (v, r) => {
                const d = isoToDayjs(v || r.po_date);
                return d ? d.format('DD-MMM-YYYY') : '—';
              }
            },
            {
              title: 'Supplier', dataIndex: 'supplierName',
              render: (v, r) => v || r.supplier_name || '—'
            },
            {
              title: 'Status', dataIndex: 'status', width: 130,
              render: (v) => <Tag color={statusColor(v)}>{statusLabel(v) || v}</Tag>
            },
            {
              title: '', dataIndex: '_pick', width: 80,
              render: (_v, r) => (
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    onPick({
                      id:              r.id || r.po_id,
                      poNo:            r.poNo || r.po_no,
                      supplierId:      r.supplierId || r.supplier_id,
                      supplierName:    r.supplierName || r.supplier_name,
                      supplierGstin:   r.supplierGstin || r.supplier_gstin,
                      supplierContact: r.supplierContact || r.supplier_contact,
                      supplierAddress: r.supplierAddress || r.supplier_address
                    });
                    setOpen(false);
                  }}
                >
                  Use
                </Button>
              )
            }
          ]}
        />
      </Modal>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// GRN picker — used in /new mode to attach a source GRN (3-way).
// ─────────────────────────────────────────────────────────────────
function GoodsReceiptPicker({ value, onPick, disabled }) {
  const [open, setOpen]       = useState(false);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows]       = useState([]);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = {
        page:     1,
        pageSize: 25,
        status:   'ACCEPTED,PARTIALLY_ACCEPTED'
      };
      if (q) params.search = q;
      const out = await goodsReceiptsApi.list(params);
      const list = Array.isArray(out)
        ? out
        : (out && Array.isArray(out.rows) ? out.rows
          : (out && Array.isArray(out.data) ? out.data : []));
      setRows(list);
    } catch (e) {
      message.error(errMessage(e, 'Failed to load goods receipts.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(keyword.trim()), 220);
    return () => clearTimeout(t);
  }, [open, keyword, search]);

  return (
    <>
      <Input
        readOnly
        value={value || ''}
        disabled={disabled}
        placeholder="Click to pick a GRN…"
        suffix={
          <Button
            type="text"
            size="small"
            icon={<SearchOutlined />}
            disabled={disabled}
            onClick={() => setOpen(true)}
          />
        }
        onClick={() => { if (!disabled) setOpen(true); }}
      />
      <Modal
        open={open}
        title="Select Goods Receipt"
        onCancel={() => setOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder="Search by GRN number, supplier…"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={(v) => search(v.trim())}
          className="grn-po-picker__search"
        />
        <Table
          className="grn-po-picker__grid"
          size="small"
          loading={loading}
          rowKey={(r) => r.id || r.grn_id}
          dataSource={rows}
          pagination={false}
          scroll={{ y: 360 }}
          columns={[
            {
              title: 'GRN No', dataIndex: 'grnNo', width: 140,
              render: (v, r) => v || r.grn_no || '—'
            },
            {
              title: 'GRN Date', dataIndex: 'grnDate', width: 110,
              render: (v, r) => {
                const d = isoToDayjs(v || r.grn_date);
                return d ? d.format('DD-MMM-YYYY') : '—';
              }
            },
            {
              title: 'Supplier', dataIndex: 'supplierName',
              render: (v, r) => v || r.supplier_name || '—'
            },
            {
              title: 'Status', dataIndex: 'status', width: 130,
              render: (v) => <Tag color={statusColor(v)}>{statusLabel(v) || v}</Tag>
            },
            {
              title: '', dataIndex: '_pick', width: 80,
              render: (_v, r) => (
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    onPick({
                      id:              r.id || r.grn_id,
                      grnNo:           r.grnNo || r.grn_no,
                      poId:            r.poId || r.po_id,
                      poNumberSnapshot: r.poNumberSnapshot || r.po_number_snapshot,
                      supplierId:      r.supplierId || r.supplier_id,
                      supplierName:    r.supplierName || r.supplier_name,
                      supplierGstin:   r.supplierGstin || r.supplier_gstin
                    });
                    setOpen(false);
                  }}
                >
                  Use
                </Button>
              )
            }
          ]}
        />
      </Modal>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main page component.
// ─────────────────────────────────────────────────────────────────
export default function PurchaseBillFormPage() {
  const { id, poId, grnId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isFromPo   = Boolean(poId);
  const isFromGrn  = Boolean(grnId);
  const isEdit     = Boolean(id);
  const isCreate   = !isEdit;

  const [form]                = Form.useForm();
  const [header, setHeader]   = useState(() => makeEmptyHeader());
  const [lines,  setLines]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [acting,  setActing]  = useState(false);

  const [cancelOpen,   setCancelOpen]   = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [previewOpen,  setPreviewOpen]  = useState(false);
  const [previewBody,  setPreviewBody]  = useState(null);

  const [balanceHint, setBalanceHint] = useState(null);

  // Ref for async readback in callbacks that capture stale state.
  const headerRef = useRef(header);
  headerRef.current = header;

  // ── Capability gates ──────────────────────────────────────────
  const isEditable = useMemo(() => {
    if (isCreate || isFromPo || isFromGrn) return true;
    return canEditPurchaseBill(header.status);
  }, [isCreate, isFromPo, isFromGrn, header.status]);

  const isLocked = !isEditable;

  // ── Loaders ────────────────────────────────────────────────────
  const hydrateFromApi = useCallback((raw) => {
    const norm = normalizePurchaseBill(raw);
    const hydrated = Object.assign({}, norm, {
      billDate:            isoToDayjs(norm.billDate),
      supplierInvoiceDate: isoToDayjs(norm.supplierInvoiceDate)
    });
    setHeader(hydrated);
    setLines(norm.lines || []);
    form.setFieldsValue(headerToFormValues(hydrated));
  }, [form]);

  const loadBill = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const raw = await purchaseBillApi.getById(id);
      hydrateFromApi(raw);
    } catch (e) {
      message.error(errMessage(e, 'Failed to load purchase bill.'));
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, hydrateFromApi]);

  const loadFromPo = useCallback(async () => {
    if (!isFromPo) return;
    setLoading(true);
    try {
      const [po, balance] = await Promise.all([
        purchaseOrdersApi.get(poId),
        purchaseBillApi.billableBalanceForPo(poId).catch(() => null)
      ]);
      if (!po) {
        message.error('Purchase order not found.');
        return;
      }
      const poLines = Array.isArray(po.lines) ? po.lines : [];
      const supplierState = po.supplierStateCode || po.supplier_state_code || '';
      const companyState  = po.companyStateCode  || po.company_state_code  || '';
      const seededLines = buildBillFromPoLines(poLines, {
        supplier_state_code: supplierState,
        company_state_code:  companyState
      });
      const draftHeader = Object.assign({}, makeEmptyHeader(), {
        purchaseOrderId:     po.id || po.po_id || poId,
        poNumberSnapshot:    po.poNo || po.po_no || null,
        poDateSnapshot:      po.poDate || po.po_date || null,
        matchingType:        'TWO_WAY',
        supplierId:          po.supplierId || po.supplier_id || null,
        supplierName:        po.supplierName || po.supplier_name || '',
        supplierGstin:       po.supplierGstin || po.supplier_gstin || '',
        supplierStateCode:   supplierState,
        companyStateCode:    companyState,
        supplierContact:     po.supplierContact || po.supplier_contact || '',
        supplierAddress:     po.supplierAddress || po.supplier_address || ''
      });
      setHeader(draftHeader);
      setLines(seededLines);
      setBalanceHint(balance);
      form.setFieldsValue(headerToFormValues(draftHeader));
    } catch (e) {
      message.error(errMessage(e, 'Failed to seed from purchase order.'));
    } finally {
      setLoading(false);
    }
  }, [isFromPo, poId, form]);

  const loadFromGrn = useCallback(async () => {
    if (!isFromGrn) return;
    setLoading(true);
    try {
      const [grn, balance] = await Promise.all([
        goodsReceiptsApi.get(grnId),
        purchaseBillApi.billableBalanceForGrn(grnId).catch(() => null)
      ]);
      if (!grn) {
        message.error('Goods receipt not found.');
        return;
      }
      const grnLines = Array.isArray(grn.lines) ? grn.lines : [];
      const supplierState = grn.supplierStateCode || grn.supplier_state_code || '';
      const companyState  = grn.companyStateCode  || grn.company_state_code  || '';
      const seededLines = buildBillFromGrnLines(grnLines, {
        supplier_state_code: supplierState,
        company_state_code:  companyState
      });
      const draftHeader = Object.assign({}, makeEmptyHeader(), {
        goodsReceiptId:      grn.id || grn.grn_id || grnId,
        grnNumberSnapshot:   grn.grnNo || grn.grn_no || null,
        grnDateSnapshot:     grn.grnDate || grn.grn_date || null,
        purchaseOrderId:     grn.poId || grn.po_id || null,
        poNumberSnapshot:    grn.poNumberSnapshot || grn.po_number_snapshot || null,
        poDateSnapshot:      grn.poDateSnapshot || grn.po_date_snapshot || null,
        matchingType:        'THREE_WAY',
        supplierId:          grn.supplierId || grn.supplier_id || null,
        supplierName:        grn.supplierName || grn.supplier_name || '',
        supplierGstin:       grn.supplierGstin || grn.supplier_gstin || '',
        supplierStateCode:   supplierState,
        companyStateCode:    companyState,
        supplierContact:     grn.supplierContact || grn.supplier_contact || '',
        supplierAddress:     grn.supplierAddress || grn.supplier_address || ''
      });
      setHeader(draftHeader);
      setLines(seededLines);
      setBalanceHint(balance);
      form.setFieldsValue(headerToFormValues(draftHeader));
    } catch (e) {
      message.error(errMessage(e, 'Failed to seed from goods receipt.'));
    } finally {
      setLoading(false);
    }
  }, [isFromGrn, grnId, form]);

  useEffect(() => {
    if (isEdit) {
      loadBill();
    } else if (isFromPo) {
      loadFromPo();
    } else if (isFromGrn) {
      loadFromGrn();
    } else {
      form.setFieldsValue(headerToFormValues(headerRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, poId, grnId, location.pathname]);

  // ── Tax context (drives CGST/SGST vs IGST split live) ─────────
  const taxContext = useMemo(() => ({
    supplier_state_code: header.supplierStateCode || '',
    company_state_code:  header.companyStateCode  || ''
  }), [header.supplierStateCode, header.companyStateCode]);

  // Recompute all lines whenever the tax context flips between
  // intra-state / inter-state — the operator may correct a state code
  // after seeding lines and we want the split to follow.
  useEffect(() => {
    setLines((prev) => prev.map(
      (ln) => calculatePurchaseBillLine(ln, taxContext)
    ));
  }, [taxContext]);

  // ── PO / GRN pickers (new mode only) ──────────────────────────
  const handlePickPo = useCallback(async (picked) => {
    if (!picked || !picked.id) return;
    setLoading(true);
    try {
      const [po, balance] = await Promise.all([
        purchaseOrdersApi.get(picked.id),
        purchaseBillApi.billableBalanceForPo(picked.id).catch(() => null)
      ]);
      const poLines = Array.isArray(po?.lines) ? po.lines : [];
      const supplierState = po?.supplierStateCode || po?.supplier_state_code
        || headerRef.current.supplierStateCode || '';
      const companyState  = po?.companyStateCode  || po?.company_state_code
        || headerRef.current.companyStateCode || '';
      const seededLines = buildBillFromPoLines(poLines, {
        supplier_state_code: supplierState,
        company_state_code:  companyState
      });
      const next = Object.assign({}, headerRef.current, {
        purchaseOrderId:  po?.id || picked.id,
        poNumberSnapshot: po?.poNo || po?.po_no || picked.poNo || null,
        poDateSnapshot:   po?.poDate || po?.po_date || null,
        supplierId:       po?.supplierId || po?.supplier_id || picked.supplierId,
        supplierName:     po?.supplierName || po?.supplier_name || picked.supplierName || '',
        supplierGstin:    po?.supplierGstin || po?.supplier_gstin || picked.supplierGstin || '',
        supplierStateCode: supplierState,
        companyStateCode:  companyState,
        supplierContact:  po?.supplierContact || po?.supplier_contact || '',
        supplierAddress:  po?.supplierAddress || po?.supplier_address || ''
      });
      setHeader(next);
      setLines(seededLines);
      setBalanceHint(balance);
      form.setFieldsValue(headerToFormValues(next));
    } catch (e) {
      message.error(errMessage(e, 'Failed to load purchase order.'));
    } finally {
      setLoading(false);
    }
  }, [form]);

  const handlePickGrn = useCallback(async (picked) => {
    if (!picked || !picked.id) return;
    setLoading(true);
    try {
      const [grn, balance] = await Promise.all([
        goodsReceiptsApi.get(picked.id),
        purchaseBillApi.billableBalanceForGrn(picked.id).catch(() => null)
      ]);
      const grnLines = Array.isArray(grn?.lines) ? grn.lines : [];
      const supplierState = grn?.supplierStateCode || grn?.supplier_state_code
        || headerRef.current.supplierStateCode || '';
      const companyState  = grn?.companyStateCode  || grn?.company_state_code
        || headerRef.current.companyStateCode || '';
      const seededLines = buildBillFromGrnLines(grnLines, {
        supplier_state_code: supplierState,
        company_state_code:  companyState
      });
      const next = Object.assign({}, headerRef.current, {
        goodsReceiptId:    grn?.id || picked.id,
        grnNumberSnapshot: grn?.grnNo || grn?.grn_no || picked.grnNo || null,
        grnDateSnapshot:   grn?.grnDate || grn?.grn_date || null,
        purchaseOrderId:   grn?.poId || grn?.po_id || picked.poId
                              || headerRef.current.purchaseOrderId,
        poNumberSnapshot:  grn?.poNumberSnapshot || grn?.po_number_snapshot
                              || picked.poNumberSnapshot
                              || headerRef.current.poNumberSnapshot,
        matchingType:      'THREE_WAY',
        supplierId:        grn?.supplierId || grn?.supplier_id || picked.supplierId
                              || headerRef.current.supplierId,
        supplierName:      grn?.supplierName || grn?.supplier_name
                              || picked.supplierName
                              || headerRef.current.supplierName,
        supplierGstin:     grn?.supplierGstin || grn?.supplier_gstin
                              || picked.supplierGstin
                              || headerRef.current.supplierGstin,
        supplierStateCode: supplierState,
        companyStateCode:  companyState
      });
      setHeader(next);
      setLines(seededLines);
      setBalanceHint(balance);
      form.setFieldsValue(headerToFormValues(next));
    } catch (e) {
      message.error(errMessage(e, 'Failed to load goods receipt.'));
    } finally {
      setLoading(false);
    }
  }, [form]);

  // ── Line patcher — every edit reroutes through
  //    calculatePurchaseBillLine so derived math refreshes instantly.
  const patchLine = useCallback((clientRowId, partial) => {
    setLines((prev) => prev.map((ln) => {
      if (ln._clientRowId !== clientRowId) return ln;
      const merged = Object.assign({}, ln, partial, { _dirty: true });
      return Object.assign(
        {},
        calculatePurchaseBillLine(merged, taxContext),
        { _clientRowId: ln._clientRowId, _dirty: true }
      );
    }));
  }, [taxContext]);

  const addLine = useCallback(() => {
    setLines((prev) => [...prev, makeEmptyLine()]);
  }, []);

  const removeLine = useCallback((clientRowId) => {
    setLines((prev) => prev.filter((ln) => ln._clientRowId !== clientRowId));
  }, []);

  // ── Per-line validation snapshot — drives inline cell errors. ─
  const lineErrors = useMemo(() => {
    const out = {};
    for (const ln of lines) {
      if (!ln || !ln._clientRowId) continue;
      const errs = {};
      const qtyV  = Number(ln.bill_qty)  || 0;
      const rate  = Number(ln.bill_rate) || 0;
      const disc  = Number(ln.discount_percent) || 0;
      const gst   = Number(ln.gst_rate) || 0;
      const type  = String(ln.item_type || '').toUpperCase();
      if (qtyV <= 0) errs.bill_qty = 'Qty must be > 0.';
      if (rate < 0)  errs.bill_rate = 'Rate cannot be negative.';
      if (disc < 0 || disc > 100) errs.discount_percent = 'Discount must be 0–100.';
      if (gst < GST_RATE_MIN || gst > GST_RATE_MAX) {
        errs.gst_rate = `GST must be between ${GST_RATE_MIN} and ${GST_RATE_MAX}.`;
      }
      if (GLASS_TYPES.has(type)) {
        const w = Number(ln.bill_width_mm)  || 0;
        const h = Number(ln.bill_height_mm) || 0;
        if (w <= 0) errs.bill_width_mm  = 'Width required for glass.';
        if (h <= 0) errs.bill_height_mm = 'Height required for glass.';
      } else if (PROFILE_TYPES.has(type)) {
        const uom = String(ln.uom || '').toUpperCase();
        if (LENGTH_UOMS.has(uom)) {
          const len = Number(ln.bill_length_value) || 0;
          if (len <= 0) errs.bill_length_value = 'Length required.';
        }
      }
      if (Object.keys(errs).length > 0) out[ln._clientRowId] = errs;
    }
    return out;
  }, [lines]);

  const hasLineErrors = Object.keys(lineErrors).length > 0;

  // ── Summary panel ─────────────────────────────────────────────
  const summary = useMemo(
    () => calculatePurchaseBillTotals(lines, {
      supplier_state_code: header.supplierStateCode,
      company_state_code:  header.companyStateCode,
      round_off:           Number(header.roundOff) || 0
    }),
    [lines, header.supplierStateCode, header.companyStateCode, header.roundOff]
  );

  // Which optional column sets are visible (at least one line of that
  // family present — keeps the grid horizontally calm for mixed POs).
  const showGlassCols   = useMemo(
    () => lines.some((l) => GLASS_TYPES.has(String(l.item_type || '').toUpperCase())),
    [lines]
  );
  const showProfileCols = useMemo(
    () => lines.some((l) => PROFILE_TYPES.has(String(l.item_type || '').toUpperCase())),
    [lines]
  );

  // ── Match panel ───────────────────────────────────────────────
  const matchCounts = useMemo(
    () => summarizeMatchResults(header.matchResults),
    [header.matchResults]
  );

  // ── Snapshot for payload builder ──────────────────────────────
  function snapshot() {
    const v = form.getFieldsValue();
    return Object.assign({}, header, v, {
      id:                  header.id,
      status:              header.status,
      billDate:            v.billDate || header.billDate,
      supplierInvoiceDate: v.supplierInvoiceDate || header.supplierInvoiceDate,
      matchingType:        v.matchingType || header.matchingType,
      supplierName:        v.supplierName || header.supplierName,
      supplierGstin:       v.supplierGstin || header.supplierGstin,
      supplierStateCode:   v.supplierStateCode || header.supplierStateCode,
      companyStateCode:    v.companyStateCode  || header.companyStateCode,
      supplierInvoiceNo:   v.supplierInvoiceNo || header.supplierInvoiceNo,
      roundOff:            Number(v.roundOff) || 0,
      remarks:             v.remarks || '',
      purchaseOrderId:     header.purchaseOrderId,
      goodsReceiptId:      header.goodsReceiptId,
      lines
    });
  }

  // ── Save Draft ────────────────────────────────────────────────
  async function saveDraft() {
    if (isLocked) {
      message.warning('This purchase bill is locked.');
      return;
    }
    if (hasLineErrors) {
      message.error('Fix the highlighted line errors before saving.');
      return;
    }
    try {
      await form.validateFields();
    } catch (_) {
      message.error('Please fix the highlighted header fields.');
      return;
    }
    const state   = snapshot();
    const payload = buildPurchaseBillPayload(state);
    const { ok, errors } = validatePurchaseBill(payload);
    if (!ok) {
      message.error(errors[0]);
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await purchaseBillApi.update(header.id, payload);
      } else if (isFromPo && !header.id) {
        saved = await purchaseBillApi.createFromPo(poId, payload);
      } else if (isFromGrn && !header.id) {
        saved = await purchaseBillApi.createFromGrn(grnId, payload);
      } else {
        saved = await purchaseBillApi.create(payload);
      }
      const norm = normalizePurchaseBill(saved);
      message.success(isEdit
        ? `Purchase bill ${norm.billNo || ''} updated.`
        : `Purchase bill ${norm.billNo || ''} saved as draft.`);
      if (!isEdit && norm.id) {
        navigate(`/erp/purchase/bills/${norm.id}`, { replace: true });
      } else {
        hydrateFromApi(saved);
      }
    } catch (e) {
      message.error(errMessage(e, 'Failed to save purchase bill.'));
    } finally {
      setSaving(false);
    }
  }

  // ── Preview ───────────────────────────────────────────────────
  async function openPreview() {
    if (hasLineErrors) {
      message.error('Fix the highlighted line errors before previewing.');
      return;
    }
    try {
      await form.validateFields();
    } catch (_) {
      message.error('Please fix the highlighted header fields.');
      return;
    }
    const state   = snapshot();
    const payload = buildPurchaseBillPayload(state);
    setActing(true);
    try {
      const body = await purchaseBillApi.preview(payload);
      setPreviewBody(body);
      setPreviewOpen(true);
    } catch (e) {
      message.error(errMessage(e, 'Failed to preview purchase bill.'));
    } finally {
      setActing(false);
    }
  }

  // ── Match (DRAFT / MISMATCH_HOLD → MATCHED or MISMATCH_HOLD) ──
  async function matchBill() {
    if (!header.id) {
      message.warning('Save the draft before running matching.');
      return;
    }
    if (!canMatchPurchaseBill(header.status)) {
      message.warning('Only DRAFT or MISMATCH_HOLD bills can be matched.');
      return;
    }
    Modal.confirm({
      title: 'Run PO/GRN matching against this bill?',
      icon: <ExclamationCircleOutlined />,
      content:
        'The backend will compute qty / rate / tax / amount deltas '
        + 'and either move the bill to MATCHED or mark it MISMATCH_HOLD '
        + 'with per-line findings. No accounting or stock posting yet.',
      okText: 'Run match',
      cancelText: 'Back',
      onOk: async () => {
        setActing(true);
        try {
          const out  = await purchaseBillApi.match(header.id);
          const norm = normalizePurchaseBill(out);
          message.success(
            `Bill ${norm.billNo || ''} ${statusLabel(norm.status)}.`);
          hydrateFromApi(out);
        } catch (e) {
          message.error(errMessage(e, 'Failed to run matching.'));
        } finally {
          setActing(false);
        }
      }
    });
  }

  // ── Approve (MATCHED → APPROVED) ──────────────────────────────
  async function approveBill() {
    if (!canApprovePurchaseBill(header.status)) {
      message.warning('Only MATCHED bills can be approved.');
      return;
    }
    Modal.confirm({
      title: 'Approve this purchase bill?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Approval freezes the bill from further edits and marks it ready '
        + 'for downstream posting. No supplier ledger or GST ITC entries '
        + 'are created at this step — those land in a later stage.',
      okText: 'Approve',
      cancelText: 'Back',
      onOk: async () => {
        setActing(true);
        try {
          const out  = await purchaseBillApi.approve(header.id);
          const norm = normalizePurchaseBill(out);
          message.success(`Bill ${norm.billNo || ''} approved.`);
          hydrateFromApi(out);
        } catch (e) {
          message.error(errMessage(e, 'Failed to approve purchase bill.'));
        } finally {
          setActing(false);
        }
      }
    });
  }

  // ── Cancel modal ──────────────────────────────────────────────
  function openCancelModal() {
    if (!canCancelPurchaseBill(header.status)) {
      message.warning('Only DRAFT or MISMATCH_HOLD bills can be cancelled.');
      return;
    }
    setCancelReason('');
    setCancelOpen(true);
  }

  async function confirmCancel() {
    setActing(true);
    try {
      const out  = await purchaseBillApi.cancel(header.id,
        { reason: cancelReason || undefined });
      const norm = normalizePurchaseBill(out);
      message.success(`Purchase bill ${norm.billNo || ''} cancelled.`);
      setCancelOpen(false);
      hydrateFromApi(out);
    } catch (e) {
      message.error(errMessage(e, 'Failed to cancel purchase bill.'));
    } finally {
      setActing(false);
    }
  }

  // ── Grid columns ──────────────────────────────────────────────
  const columns = useMemo(() => {
    const numCellProps      = { className: 'grn-cell-num' };
    const readonlyCellProps = { className: 'grn-cell-readonly grn-cell-num' };

    function cellError(cid, field) {
      const e = lineErrors[cid];
      return e && e[field] ? e[field] : null;
    }

    function errWrap(content, errMsg) {
      if (!errMsg) return content;
      return (
        <Tooltip title={errMsg}>
          <div className="grn-cell-error">{content}</div>
        </Tooltip>
      );
    }

    const cols = [
      {
        title: 'Item Type', dataIndex: 'item_type', width: 130, fixed: 'left',
        render: (v, row) => (
          <Select
            size="small"
            variant="borderless"
            value={v || undefined}
            disabled={isLocked}
            style={{ width: '100%' }}
            options={ITEM_TYPE_OPTIONS}
            placeholder="Type"
            onChange={(val) => patchLine(row._clientRowId, {
              item_type: val,
              // Reset item identity when crossing the product/service
              // boundary (product must be re-picked from master; SERVICE/
              // OTHER drops the master id and reverts to free-text).
              item_id: null, item_code: null, item_name: ''
            })}
          />
        )
      },
      {
        title: 'Item Desc', dataIndex: 'item_name', width: 240,
        render: (v, row) => {
          const curName = row.item_name || row.itemName || '';
          const curId   = row.item_id   || row.itemId   || null;
          const sourced = Boolean(
            row.purchase_order_line_id || row.purchaseOrderLineId
            || row.goods_receipt_line_id || row.goodsReceiptLineId);
          // Lines pulled from a PO/GRN inherit their (already master-bound)
          // item — keep them read-only.
          if (sourced) {
            return (
              <Input size="small" variant="borderless"
                value={curName} disabled maxLength={500} />
            );
          }
          // SERVICE / OTHER stay free-text; products must be master-picked.
          if (MASTER_OPTIONAL_TYPES.has(row.item_type)) {
            return (
              <Input
                size="small"
                variant="borderless"
                value={curName}
                disabled={isLocked}
                maxLength={500}
                placeholder="Item description"
                onChange={(e) => patchLine(row._clientRowId,
                  { item_id: null, item_code: null, item_name: e.target.value })}
              />
            );
          }
          return (
            <ItemMasterSelect
              value={curId || undefined}
              label={curName}
              disabled={isLocked}
              onClear={() => patchLine(row._clientRowId,
                { item_id: null, item_code: null, item_name: '' })}
              onSelectItem={(id, item) => patchLine(row._clientRowId, {
                item_id:   id,
                item_code: item ? (item.itemCode || null) : null,
                item_name: item ? (item.itemName || curName) : curName,
                uom: row.uom || (item && item.defaultUomCode) || 'PCS',
                rate: Number(row.rate) > 0
                  ? row.rate : (item && Number(item.purchaseRate)) || row.rate,
                bill_rate: Number(row.rate) > 0
                  ? row.rate : (item && Number(item.purchaseRate)) || row.rate,
                gst_rate: Number(row.gst_rate) > 0
                  ? row.gst_rate : (item && Number(item.gstRatePercent)) || row.gst_rate
              })}
            />
          );
        }
      }
    ];

    // PO / GRN reference column (compact, read-only).
    cols.push(
      {
        title: 'PO Qty', dataIndex: '_po_qty', width: 80,
        onCell: () => readonlyCellProps,
        render: (_v, row) => {
          const v = row.ordered_qty ?? row.orderedQty ?? row.po_qty ?? row.poQty;
          if (v === undefined || v === null) return '';
          return qty(v);
        }
      },
      {
        title: 'GRN Acc Qty', dataIndex: '_grn_acc_qty', width: 90,
        onCell: () => readonlyCellProps,
        render: (_v, row) => {
          const v = row.accepted_qty ?? row.acceptedQty;
          if (v === undefined || v === null) return '';
          return qty(v);
        }
      }
    );

    if (showGlassCols) {
      cols.push(
        {
          title: 'Bill W', dataIndex: 'bill_width_mm', width: 80,
          onCell: () => numCellProps,
          render: (v, row) => {
            if (!GLASS_TYPES.has(String(row.item_type || '').toUpperCase())) return null;
            const err = cellError(row._clientRowId, 'bill_width_mm');
            return errWrap(
              <InputNumber
                size="small"
                variant="borderless"
                controls={false}
                min={0}
                step={1}
                status={err ? 'error' : undefined}
                value={v ?? null}
                disabled={isLocked}
                style={{ width: '100%' }}
                onChange={(n) => patchLine(row._clientRowId,
                  { bill_width_mm: n == null ? 0 : Number(n) })}
              />,
              err
            );
          }
        },
        {
          title: 'Bill H', dataIndex: 'bill_height_mm', width: 80,
          onCell: () => numCellProps,
          render: (v, row) => {
            if (!GLASS_TYPES.has(String(row.item_type || '').toUpperCase())) return null;
            const err = cellError(row._clientRowId, 'bill_height_mm');
            return errWrap(
              <InputNumber
                size="small"
                variant="borderless"
                controls={false}
                min={0}
                step={1}
                status={err ? 'error' : undefined}
                value={v ?? null}
                disabled={isLocked}
                style={{ width: '100%' }}
                onChange={(n) => patchLine(row._clientRowId,
                  { bill_height_mm: n == null ? 0 : Number(n) })}
              />,
              err
            );
          }
        }
      );
    }

    cols.push({
      title: 'Bill Qty', dataIndex: 'bill_qty', width: 90,
      onCell: () => numCellProps,
      render: (v, row) => {
        const err = cellError(row._clientRowId, 'bill_qty');
        return errWrap(
          <InputNumber
            size="small"
            variant="borderless"
            controls={false}
            min={0}
            step={1}
            status={err ? 'error' : undefined}
            value={v ?? null}
            disabled={isLocked}
            style={{ width: '100%' }}
            onChange={(n) => patchLine(row._clientRowId,
              { bill_qty: n == null ? 0 : Number(n) })}
          />,
          err
        );
      }
    });

    if (showGlassCols) {
      cols.push(
        {
          title: 'Bill SQMT', dataIndex: 'bill_sqmt', width: 90,
          onCell: () => readonlyCellProps,
          render: (v, row) => GLASS_TYPES.has(
            String(row.item_type || '').toUpperCase()) ? dim(v) : ''
        },
        {
          title: 'Bill SQFT', dataIndex: 'bill_sqft', width: 90,
          onCell: () => readonlyCellProps,
          render: (v, row) => GLASS_TYPES.has(
            String(row.item_type || '').toUpperCase()) ? dim(v) : ''
        }
      );
    }

    if (showProfileCols) {
      cols.push(
        {
          title: 'Bill Length', dataIndex: 'bill_length_value', width: 100,
          onCell: () => numCellProps,
          render: (v, row) => {
            if (!PROFILE_TYPES.has(String(row.item_type || '').toUpperCase())) return null;
            const err = cellError(row._clientRowId, 'bill_length_value');
            return errWrap(
              <InputNumber
                size="small"
                variant="borderless"
                controls={false}
                min={0}
                step={0.01}
                status={err ? 'error' : undefined}
                value={v ?? null}
                disabled={isLocked}
                style={{ width: '100%' }}
                onChange={(n) => patchLine(row._clientRowId,
                  { bill_length_value: n == null ? 0 : Number(n) })}
              />,
              err
            );
          }
        },
        {
          title: 'Bill Total L', dataIndex: 'bill_total_length', width: 100,
          onCell: () => readonlyCellProps,
          render: (v, row) => PROFILE_TYPES.has(
            String(row.item_type || '').toUpperCase()) ? dim(v) : ''
        }
      );
    }

    cols.push(
      {
        title: 'UOM', dataIndex: 'uom', width: 90,
        render: (v, row) => (
          <Select
            size="small"
            variant="borderless"
            value={v || undefined}
            disabled={isLocked}
            style={{ width: '100%' }}
            showSearch
            options={UOM_OPTIONS}
            filterOption={(input, opt) =>
              String(opt?.value || '').toLowerCase()
                .includes(input.toLowerCase())}
            onChange={(val) => patchLine(row._clientRowId, { uom: val })}
            placeholder="UOM"
          />
        )
      },
      {
        title: 'Rate', dataIndex: 'bill_rate', width: 100,
        onCell: () => numCellProps,
        render: (v, row) => {
          const err = cellError(row._clientRowId, 'bill_rate');
          return errWrap(
            <InputNumber
              size="small"
              variant="borderless"
              controls={false}
              min={0}
              step={0.01}
              status={err ? 'error' : undefined}
              value={v ?? null}
              disabled={isLocked}
              style={{ width: '100%' }}
              onChange={(n) => patchLine(row._clientRowId,
                { bill_rate: n == null ? 0 : Number(n) })}
            />,
            err
          );
        }
      },
      {
        title: 'Gross', dataIndex: 'gross_amount', width: 110,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      {
        title: 'Disc %', dataIndex: 'discount_percent', width: 80,
        onCell: () => numCellProps,
        render: (v, row) => {
          const err = cellError(row._clientRowId, 'discount_percent');
          return errWrap(
            <InputNumber
              size="small"
              variant="borderless"
              controls={false}
              min={0}
              max={100}
              step={0.01}
              status={err ? 'error' : undefined}
              value={v ?? null}
              disabled={isLocked}
              style={{ width: '100%' }}
              onChange={(n) => patchLine(row._clientRowId,
                { discount_percent: n == null ? 0 : Number(n) })}
            />,
            err
          );
        }
      },
      {
        title: 'Disc Amt', dataIndex: 'discount_amount', width: 100,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      {
        title: 'Taxable', dataIndex: 'taxable_amount', width: 110,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      {
        title: 'GST %', dataIndex: 'gst_rate', width: 80,
        onCell: () => numCellProps,
        render: (v, row) => {
          const err = cellError(row._clientRowId, 'gst_rate');
          return errWrap(
            <InputNumber
              size="small"
              variant="borderless"
              controls={false}
              min={GST_RATE_MIN}
              max={GST_RATE_MAX}
              step={0.5}
              status={err ? 'error' : undefined}
              value={v ?? null}
              disabled={isLocked}
              style={{ width: '100%' }}
              onChange={(n) => patchLine(row._clientRowId,
                { gst_rate: n == null ? 0 : Number(n) })}
            />,
            err
          );
        }
      },
      {
        title: 'CGST', dataIndex: 'cgst_amount', width: 100,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      {
        title: 'SGST', dataIndex: 'sgst_amount', width: 100,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      {
        title: 'IGST', dataIndex: 'igst_amount', width: 100,
        onCell: () => readonlyCellProps,
        render: (v) => money(v)
      },
      {
        title: 'Line Total', dataIndex: 'total_amount', width: 120,
        onCell: () => readonlyCellProps,
        render: (v) => <strong>{money(v)}</strong>
      },
      {
        title: 'Match', dataIndex: 'match_status', width: 120,
        render: (v) => {
          const key = String(v || 'PENDING').toUpperCase();
          return <Tag color={matchStatusColor(key)}>{matchStatusLabel(key)}</Tag>;
        }
      },
      {
        title: 'Remarks', dataIndex: 'remarks', width: 180,
        render: (v, row) => (
          <Input
            size="small"
            variant="borderless"
            value={v || ''}
            disabled={isLocked}
            maxLength={500}
            placeholder="Notes"
            onChange={(e) => patchLine(row._clientRowId,
              { remarks: e.target.value })}
          />
        )
      },
      {
        title: '', dataIndex: '_actions', width: 44, fixed: 'right',
        render: (_, row) => {
          if (isLocked) return null;
          // Lines tied to a PO/GRN line cannot be removed (the backend
          // would lose its delta target). Operators can zero the qty.
          const linked = row.purchase_order_line_id || row.purchaseOrderLineId
            || row.goods_receipt_line_id || row.goodsReceiptLineId;
          if (linked) {
            return (
              <Tooltip title="Linked lines cannot be removed.">
                <Button size="small" type="text" disabled
                  icon={<LockOutlined />} />
              </Tooltip>
            );
          }
          return (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'delete',
                    label: 'Delete line',
                    icon: <DeleteOutlined />,
                    danger: true
                  }
                ],
                onClick: ({ key }) => {
                  if (key === 'delete') removeLine(row._clientRowId);
                }
              }}
            >
              <Button size="small" type="text" icon={<MoreOutlined />} />
            </Dropdown>
          );
        }
      }
    );

    return cols;
  }, [isLocked, patchLine, removeLine, lineErrors,
      showGlassCols, showProfileCols]);

  // ── Action gates for the toolbar ───────────────────────────────
  const showMatch    = isEdit && canMatchPurchaseBill(header.status);
  const showApprove  = isEdit && canApprovePurchaseBill(header.status);
  const showCancelBn = isEdit && canCancelPurchaseBill(header.status);

  const statusTag = (
    <Tag color={statusColor(header.status || 'DRAFT')}>
      {statusLabel(header.status || 'DRAFT')}
    </Tag>
  );

  const matchTag = (
    <Tag color={matchStatusColor(header.matchStatus || 'PENDING')}>
      Match: {matchStatusLabel(header.matchStatus || 'PENDING')}
    </Tag>
  );

  const dirtyCount = useMemo(
    () => lines.filter((l) => l._dirty).length, [lines]);

  // Billable-balance hint (header subtitle).
  const balanceLabel = useMemo(() => {
    if (!balanceHint || typeof balanceHint !== 'object') return null;
    const pending = balanceHint.pendingAmount ?? balanceHint.pending_amount;
    if (pending === undefined || pending === null) return null;
    const src = isFromGrn || header.goodsReceiptId ? 'GRN' : 'PO';
    return `Source ${src} billable balance ₹${money(pending)}`;
  }, [balanceHint, isFromGrn, header.goodsReceiptId]);

  return (
    <div className="grn-shell">
      {/* ── Header strip ─────────────────────────────────────── */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><FileDoneOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">
              {isEdit
                ? (header.billNo
                    ? `Purchase Bill — ${header.billNo}`
                    : 'Purchase Bill')
                : (isFromPo
                    ? 'New Purchase Bill (from PO)'
                    : (isFromGrn
                        ? 'New Purchase Bill (from GRN)'
                        : 'New Purchase Bill'))}
            </span>
            <span className="grn-headstrip__subtitle">
              {isLocked
                ? 'Read-only — this bill is no longer editable.'
                : (balanceLabel
                    || 'Edit header + line cells — derived totals refresh live.')}
            </span>
          </div>
        </div>
        <Space size={6}>
          {statusTag}
          {matchTag}
          {!isLocked && dirtyCount > 0 && (
            <span className="grn-stat-chip grn-stat-chip--dirty">
              <strong>{dirtyCount}</strong> unsaved
            </span>
          )}
          {isLocked && (
            <span className="grn-stat-chip">
              <LockOutlined /> locked
            </span>
          )}
        </Space>
      </div>

      {/* ── Sticky toolbar ───────────────────────────────────── */}
      <div className="grn-toolbar">
        <Button size="small" icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/erp/purchase/bills')}>
          Back to list
        </Button>
        <div className="grn-toolbar__spacer" />
        {!isLocked && (
          <Button size="small" icon={<SaveOutlined />} type="primary"
            onClick={saveDraft} loading={saving}
            disabled={hasLineErrors}>
            Save Draft
          </Button>
        )}
        {!isLocked && (
          <Button size="small" icon={<EyeOutlined />}
            onClick={openPreview} disabled={acting || hasLineErrors}>
            Preview
          </Button>
        )}
        {showMatch && (
          <Button size="small" icon={<SyncOutlined />}
            onClick={matchBill} loading={acting}>
            Match
          </Button>
        )}
        {showApprove && (
          <Button size="small" type="primary"
            icon={<CheckCircleOutlined />}
            onClick={approveBill} loading={acting}>
            Approve
          </Button>
        )}
        {showCancelBn && (
          <Button size="small" danger icon={<StopOutlined />}
            onClick={openCancelModal} disabled={acting}>
            Cancel Bill
          </Button>
        )}
      </div>

      <Spin spinning={loading}>
        {isLocked && (
          <Alert
            type="info"
            showIcon
            message={`Purchase bill is ${statusLabel(header.status)} — fields are read-only.`}
            description={
              header.status === 'CANCELLED' && header.cancellationReason
                ? `Cancellation reason: ${header.cancellationReason}`
                : null
            }
          />
        )}

        {/* ── Header form ────────────────────────────────────── */}
        <Form
          form={form}
          layout="vertical"
          disabled={isLocked}
          initialValues={headerToFormValues(header)}
          onValuesChange={(changed) => {
            // Mirror header-level state changes that affect derived
            // math (state codes, matching type, round-off) into the
            // editor state immediately so the summary panel reacts.
            if ('supplierStateCode' in changed
                || 'companyStateCode' in changed
                || 'matchingType' in changed
                || 'supplierName' in changed
                || 'supplierGstin' in changed
                || 'supplierInvoiceNo' in changed
                || 'supplierInvoiceDate' in changed
                || 'billDate' in changed
                || 'roundOff' in changed
                || 'remarks' in changed) {
              setHeader((prev) => Object.assign({}, prev, changed));
            }
          }}
        >
          <div className="grn-card">
            <div className="grn-card__title">
              <span>Bill Details</span>
              <span className="grn-stat-chip">
                {header.billNo || (isEdit ? '—' : 'Auto-generated on save')}
              </span>
            </div>

            <div className="grid grid-cols-12 gap-x-3">
              <div className="col-span-3">
                <Form.Item label="Bill No">
                  <Input
                    value={header.billNo || ''}
                    disabled
                    placeholder={isEdit ? '—' : 'Auto-generated on save'}
                  />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="Bill Date" name="billDate"
                  rules={[{ required: true, message: 'Bill date is required.' }]}>
                  <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="Supplier Invoice No" name="supplierInvoiceNo"
                  rules={[{ required: true,
                    message: 'Supplier invoice number is required.' }]}>
                  <Input maxLength={200} placeholder="Vendor invoice no" />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="Supplier Invoice Date" name="supplierInvoiceDate"
                  rules={[{ required: true,
                    message: 'Supplier invoice date is required.' }]}>
                  <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                </Form.Item>
              </div>

              <div className="col-span-4">
                <Form.Item label="Supplier (from master)"
                  tooltip="Pick a master supplier to link payables — supplier_id is what makes this bill payable bill-wise. Leave blank for a free-text supplier.">
                  <SupplierMasterSelect
                    value={header.supplierId || undefined}
                    label={header.supplierName}
                    disabled={isLocked}
                    onSelectSupplier={(sid, sup) => {
                      setHeader((prev) => Object.assign({}, prev, {
                        supplierId: sid
                      }));
                      if (sup) {
                        form.setFieldsValue({
                          supplierName:      sup.supplierName || '',
                          supplierGstin:     sup.supplierGstin || '',
                          supplierStateCode: sup.supplierStateCode || ''
                        });
                        setHeader((prev) => Object.assign({}, prev, {
                          supplierName:      sup.supplierName || prev.supplierName,
                          supplierGstin:     sup.supplierGstin || prev.supplierGstin,
                          supplierStateCode: sup.supplierStateCode || prev.supplierStateCode
                        }));
                      }
                    }}
                    onClear={() => setHeader((prev) =>
                      Object.assign({}, prev, { supplierId: null }))}
                  />
                </Form.Item>
              </div>
              <div className="col-span-4">
                <Form.Item label="Supplier" name="supplierName"
                  rules={[{ required: true,
                    message: 'Supplier name is required.' }]}>
                  <Input maxLength={200} placeholder="Supplier name" />
                </Form.Item>
              </div>
              <div className="col-span-2">
                <Form.Item label="Supplier GSTIN" name="supplierGstin">
                  <Input maxLength={15} placeholder="—" />
                </Form.Item>
              </div>
              <div className="col-span-2">
                <Form.Item label="Supplier State" name="supplierStateCode">
                  <Input maxLength={2} placeholder="e.g. 27" />
                </Form.Item>
              </div>
              <div className="col-span-2">
                <Form.Item label="Company State" name="companyStateCode">
                  <Input maxLength={2} placeholder="e.g. 27" />
                </Form.Item>
              </div>
              <div className="col-span-2">
                <Form.Item label="Matching Type" name="matchingType"
                  rules={[{ required: true,
                    message: 'Matching type is required.' }]}>
                  <Select options={MATCHING_TYPE_OPTIONS} />
                </Form.Item>
              </div>

              <div className="col-span-3">
                <Form.Item label="Source PO">
                  <PurchaseOrderPicker
                    value={header.poNumberSnapshot || ''}
                    disabled={isLocked || isEdit || isFromPo}
                    onPick={handlePickPo}
                  />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="PO No" name="poNumberSnapshot">
                  <Input value={header.poNumberSnapshot || ''}
                    disabled placeholder="—" />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="Source GRN">
                  <GoodsReceiptPicker
                    value={header.grnNumberSnapshot || ''}
                    disabled={isLocked || isEdit || isFromGrn}
                    onPick={handlePickGrn}
                  />
                </Form.Item>
              </div>
              <div className="col-span-3">
                <Form.Item label="GRN No" name="grnNumberSnapshot">
                  <Input value={header.grnNumberSnapshot || ''}
                    disabled placeholder="—" />
                </Form.Item>
              </div>

              <div className="col-span-3">
                <Form.Item label="Round Off" name="roundOff">
                  <InputNumber
                    step={0.01}
                    style={{ width: '100%' }}
                    placeholder="0.00"
                  />
                </Form.Item>
              </div>
              <div className="col-span-9">
                <Form.Item label="Remarks" name="remarks">
                  <Input.TextArea rows={2} maxLength={2000}
                    placeholder="Header notes" />
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>

        {/* ── Body: lines grid + summary + match panel ──────────── */}
        <div className="grn-body">
          <div className="grn-lines-card">
            <div className="grn-lines-card__head">
              <span className="grn-lines-title">
                Lines ({lines.length})
              </span>
              {!isLocked && !isEdit && !isFromPo && !isFromGrn && (
                <Tooltip title="Pick a Purchase Order or GRN above to seed the line grid.">
                  <Button size="small" type="link" disabled>
                    Pick source to seed lines
                  </Button>
                </Tooltip>
              )}
              {!isLocked && (
                <Button
                  size="small"
                  type="link"
                  onClick={addLine}
                >
                  Add ad-hoc line
                </Button>
              )}
            </div>
            <Table
              className="grn-grid"
              size="small"
              rowKey="_clientRowId"
              dataSource={lines}
              columns={columns}
              pagination={false}
              sticky
              scroll={{ x: 'max-content' }}
              rowClassName={(r) => {
                const cls = [];
                if (r._dirty) cls.push('grn-dirty-row');
                if (lineErrors[r._clientRowId]) cls.push('grn-error-row');
                return cls.join(' ');
              }}
              locale={{
                emptyText: isLocked
                  ? 'No lines on this purchase bill.'
                  : (header.purchaseOrderId || header.goodsReceiptId
                      ? 'No lines yet — Add ad-hoc line to start.'
                      : 'Pick a Purchase Order or GRN to seed lines.')
              }}
            />
          </div>

          {/* ── Summary + Match panel ───────────────────────────── */}
          <div className="grn-summary">
            <div className="grn-summary__title">Bill Summary</div>
            <div className="grn-summary__grid">
              <div className="grn-summary__label">Total Lines</div>
              <div className="grn-summary__value">{summary.totalLines}</div>

              <div className="grn-summary__label">Subtotal</div>
              <div className="grn-summary__value">{money(summary.subtotal)}</div>

              <div className="grn-summary__label">Discount</div>
              <div className="grn-summary__value">
                {money(summary.discount_amount)}
              </div>

              <div className="grn-summary__label">Taxable</div>
              <div className="grn-summary__value">
                {money(summary.taxable_amount)}
              </div>

              <div className="grn-summary__divider" />

              <div className="grn-summary__label">CGST</div>
              <div className="grn-summary__value">
                {money(summary.cgst_amount)}
              </div>
              <div className="grn-summary__label">SGST</div>
              <div className="grn-summary__value">
                {money(summary.sgst_amount)}
              </div>
              <div className="grn-summary__label">IGST</div>
              <div className="grn-summary__value">
                {money(summary.igst_amount)}
              </div>
              <div className="grn-summary__label">Total Tax</div>
              <div className="grn-summary__value">
                {money(summary.total_tax)}
              </div>

              <div className="grn-summary__divider" />

              <div className="grn-summary__label">Round Off</div>
              <div className="grn-summary__value">
                {money(summary.round_off)}
              </div>
              <div className="grn-summary__label">
                <strong>Grand Total</strong>
              </div>
              <div className="grn-summary__value">
                <strong>{money(summary.grand_total)}</strong>
              </div>

              <div className="grn-summary__divider" />

              <div className="grn-summary__label">Status</div>
              <div className="grn-summary__value">
                <Tag color={statusColor(header.status || 'DRAFT')}>
                  {statusLabel(header.status || 'DRAFT')}
                </Tag>
              </div>
              <div className="grn-summary__label">Match</div>
              <div className="grn-summary__value">
                <Tag color={matchStatusColor(header.matchStatus || 'PENDING')}>
                  {matchStatusLabel(header.matchStatus || 'PENDING')}
                </Tag>
              </div>
            </div>

            {/* Match-result mini panel — blockers / warnings / info */}
            {(matchCounts.blockers + matchCounts.warnings + matchCounts.info) > 0
              && (
              <div className="grn-summary__chrono">
                <Descriptions size="small" column={1} colon={false}
                  title="Match Findings">
                  {matchCounts.blockers > 0 && (
                    <Descriptions.Item label="Blockers">
                      <Tag color="error">{matchCounts.blockers}</Tag>
                    </Descriptions.Item>
                  )}
                  {matchCounts.warnings > 0 && (
                    <Descriptions.Item label="Warnings">
                      <Tag color="warning">{matchCounts.warnings}</Tag>
                    </Descriptions.Item>
                  )}
                  {matchCounts.info > 0 && (
                    <Descriptions.Item label="Info">
                      <Tag color="default">{matchCounts.info}</Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
                <ul className="grn-match-list">
                  {(header.matchResults || []).slice(0, 8).map((r, idx) => (
                    <li key={idx}>
                      <Tag color={
                        String(r.severity || '').toUpperCase() === 'BLOCKER'
                          ? 'error'
                          : (String(r.severity || '').toUpperCase() === 'WARNING'
                              ? 'warning' : 'default')
                      }>
                        {String(r.type || 'OTHER').toUpperCase()}
                      </Tag>
                      <span style={{ marginLeft: 6 }}>
                        {r.message || ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Workflow chrono for context once posted. */}
            {(header.approvedAt || header.cancelledAt) && (
              <div className="grn-summary__chrono">
                <Descriptions size="small" column={1} colon={false}>
                  {header.approvedAt && (
                    <Descriptions.Item label="Approved">
                      {dayjs(header.approvedAt).format('DD-MMM-YY HH:mm')}
                      {header.approvedBy ? ` · ${header.approvedBy}` : ''}
                    </Descriptions.Item>
                  )}
                  {header.cancelledAt && (
                    <Descriptions.Item label="Cancelled">
                      {dayjs(header.cancelledAt).format('DD-MMM-YY HH:mm')}
                      {header.cancelledBy ? ` · ${header.cancelledBy}` : ''}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}

            {/* Source links for context */}
            {(header.purchaseOrderId || header.goodsReceiptId) && (
              <div className="grn-summary__chrono">
                <Space direction="vertical" size={2}>
                  {header.purchaseOrderId && (
                    <Button type="link" size="small" icon={<LinkOutlined />}
                      onClick={() => navigate(
                        `/erp/purchase/orders/${header.purchaseOrderId}`)}>
                      Open source PO {header.poNumberSnapshot
                        ? `(${header.poNumberSnapshot})` : ''}
                    </Button>
                  )}
                  {header.goodsReceiptId && (
                    <Button type="link" size="small" icon={<LinkOutlined />}
                      onClick={() => navigate(
                        `/erp/purchase/grns/${header.goodsReceiptId}`)}>
                      Open source GRN {header.grnNumberSnapshot
                        ? `(${header.grnNumberSnapshot})` : ''}
                    </Button>
                  )}
                </Space>
              </div>
            )}
          </div>
        </div>
      </Spin>

      {/* ── Cancel modal ──────────────────────────────────────── */}
      <Modal
        open={cancelOpen}
        title="Cancel purchase bill?"
        okText="Cancel Bill"
        okType="danger"
        cancelText="Back"
        confirmLoading={acting}
        onCancel={() => setCancelOpen(false)}
        onOk={confirmCancel}
      >
        <p className="text-sm text-slate-600 mb-2">
          Cancelling marks this bill and all its lines as cancelled. Any
          linked PO / GRN billable balance is restored automatically.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={2000}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="Optional cancellation reason"
        />
      </Modal>

      {/* ── Preview modal ─────────────────────────────────────── */}
      <Modal
        open={previewOpen}
        title="Bill Preview"
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={720}
        onCancel={() => setPreviewOpen(false)}
        onOk={() => setPreviewOpen(false)}
        destroyOnClose
      >
        {previewBody ? (
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Subtotal">
              {money(previewBody.subtotal
                || previewBody.subTotal
                || previewBody.gross_amount
                || previewBody.grossAmount)}
            </Descriptions.Item>
            <Descriptions.Item label="Discount">
              {money(previewBody.discount_amount
                || previewBody.discountAmount)}
            </Descriptions.Item>
            <Descriptions.Item label="Taxable">
              {money(previewBody.taxable_amount
                || previewBody.taxableAmount)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Tax">
              {money(previewBody.total_tax
                || previewBody.totalTax)}
            </Descriptions.Item>
            <Descriptions.Item label="CGST">
              {money(previewBody.cgst_amount
                || previewBody.cgstAmount)}
            </Descriptions.Item>
            <Descriptions.Item label="SGST">
              {money(previewBody.sgst_amount
                || previewBody.sgstAmount)}
            </Descriptions.Item>
            <Descriptions.Item label="IGST">
              {money(previewBody.igst_amount
                || previewBody.igstAmount)}
            </Descriptions.Item>
            <Descriptions.Item label="Round Off">
              {money(previewBody.round_off
                || previewBody.roundOff)}
            </Descriptions.Item>
            <Descriptions.Item label="Grand Total" span={2}>
              <strong>
                {money(previewBody.grand_total
                  || previewBody.grandTotal)}
              </strong>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Alert type="info" showIcon
            message="Preview unavailable." />
        )}
      </Modal>
    </div>
  );
}
